/**
 * SAM-1298 (canvas W1–W4) — «Register your business» on the admin's login (business.capcana.app):
 *   /register           W2 Create account: name, email, password, then the code sent to the email (loginSam signup)
 *   /register/business  W3 the New business form without the menu, «Send request» (POST /business-requests);
 *                       an Estate business also picks the estate group it administers
 *   /register/sent      W4 Request sent: Processing until the tenant answers; once approved the token is refreshed
 *                       and the owner enters their business
 * Public route (Rodrigo, 30 Oct P3; same pool and /login/signup, 2 Nov, SAM-1298). The owner's token has no group
 * until the approval; the server decides everything it may do.
 * New file under the BaseUI freeze (SAM-175): Tailwind + tokens.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import * as authService from '../../services/authService';
import { processRequestQueue } from '../../api/httpClient';
import { businessRequestsApi } from '../../api/businessRequestsApi';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useS3Image } from '../../hooks/useS3Image';
import { useBusinessCategories } from '../../hooks/useBusinessCrud';
import { getStoredToken } from '../../utils/token';
import { endSession } from '../../utils/storageUtils';
import { signOut } from '../../utils/session';
import { NewBusinessFields, newBusinessCard } from '../BusinessPage/NewBusinessFields';
import { EMPTY_NEW_BUSINESS, type NewBusinessForm } from '../BusinessPage/newBusiness';
import { StatusChip } from '../BusinessPage/ActivityControls';
import { statusView } from '../support/businessRequestsLogic';
import {
  PASSWORD_RULE, businessRequestBody, formFromPayload, isEstateCategory, latestBusinessRequest, ownerStep, passwordOk,
  registerIssues, sendErrorText, signUpErrorText,
} from './registerBusinessLogic';

const inputCls = 'h-12 w-full px-3.5 rounded-[8px] border-2 border-solid border-[#eeeeee] bg-[#eeeeee] text-[15px] font-[inherit] text-[var(--ink-900)] box-border';
const labelCls = 'flex flex-col gap-1.5 text-[14px] font-medium text-[var(--ink-900)]';
const primaryBtn = 'w-full h-12 rounded-[8px] border-0 text-white text-[16px] font-semibold font-[inherit] cursor-pointer inline-flex items-center justify-center gap-2 disabled:opacity-60';
const tertiaryBtn = 'w-full h-12 rounded-[8px] border-0 bg-transparent text-[var(--ink-900)] text-[16px] font-medium font-[inherit] cursor-pointer';

/** The login's frame (logo over a 350 px column), so the owner stays on the same screen family as W1. */
const AuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenantLogo, tenantName, tenantPrimaryColor } = useTenantBranding();
  const { imageUrl } = useS3Image(tenantLogo);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-white px-4 pb-[10vh] box-border">
      <div className="flex items-center justify-center h-[120px] sm:h-[160px] mb-4">
        {imageUrl
          ? <img src={imageUrl} alt={tenantName} className="max-h-full max-w-[300px] object-contain" />
          : tenantName && !tenantLogo ? <div className="text-[22px] font-semibold" style={{ color: tenantPrimaryColor }}>{tenantName}</div> : null}
      </div>
      <div className="w-full max-w-[350px] flex flex-col gap-4">{children}</div>
    </div>
  );
};

const ErrorText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p role="alert" className="m-0 text-[14px] text-[#e54937]">{children}</p>
);

/** W2 — Create account. The code field appears once the email got it. */
const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId, tenantPrimaryColor } = useTenantBranding();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = (e: unknown) => {
    const err = e as authService.SignUpError;
    setError(signUpErrorText(err?.code ?? null, 'Something went wrong. Try again.'));
  };

  const onContinue = async () => {
    setError(null);
    if (!codeSent && !passwordOk(password)) { setError(PASSWORD_RULE); return; }
    setBusy(true);
    try {
      if (!codeSent) {
        await authService.signUp(email.trim(), password, tenantId || null);
        setCodeSent(true);
        return;
      }
      await authService.confirmSignUp(email.trim(), code.trim(), tenantId || null);
      const result = await authService.login(email.trim(), password, tenantId || null);
      if (result.result !== 'SUCCESS') {
        setError(result.result === 'ERROR' ? result.message : 'Log in to continue.');
        return;
      }
      // The name has no place in the account; it rides to W3 and goes with the request (Requested by).
      navigate('/register/business', { replace: true, state: { ownerName: name.trim() } });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(null);
    try { await authService.resendSignUpCode(email.trim(), tenantId || null); } catch (e) { fail(e); }
  };

  const ready = codeSent ? code.trim().length > 0 : !!(name.trim() && email.trim() && password);
  return (
    <AuthShell>
      <h1 className="m-0 text-[24px] font-bold text-[var(--ink-900)]">Create account</h1>
      <p className="m-0 text-[14px] text-[#6B7280]">You will be the owner of the business.</p>
      <label className={labelCls}>Name
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoComplete="name" disabled={codeSent} />
      </label>
      <label className={labelCls}>Email
        <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="email" disabled={codeSent} />
      </label>
      <label className={labelCls}>Password
        <input className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="new-password" disabled={codeSent} />
      </label>
      {codeSent && (
        <label className={labelCls}>Verification code
          <input className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code sent to your email" inputMode="numeric" autoComplete="one-time-code" />
        </label>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <button type="button" className={primaryBtn} style={{ backgroundColor: tenantPrimaryColor || '#005488' }} disabled={!ready || busy} onClick={onContinue}>
        {busy && <Loader2 size={16} className="animate-spin" aria-hidden />}
        Continue
      </button>
      {codeSent && <button type="button" className={tertiaryBtn} disabled={busy} onClick={resend}>Send the code again</button>}
      <button type="button" className={tertiaryBtn} disabled={busy} onClick={() => navigate('/')}>Back to login</button>
    </AuthShell>
  );
};

/**
 * The owner's session: the id token from the sign-in (httpClient refreshes it when it expires). With it, the request
 * queue that waits for a business is released: this account has none until the approval.
 */
const useOwnerSession = (): boolean => {
  const signedIn = !!getStoredToken();
  useEffect(() => { if (signedIn) processRequestQueue(); }, [signedIn]);
  return signedIn;
};

const logOut = () => void endSession(signOut);

/** W3 — Register your business: New business without the menu, «Send request». */
const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const ownerName = (useLocation().state as { ownerName?: string } | null)?.ownerName ?? '';
  const { tenantName } = useTenantBranding();
  const { data: categories = [] } = useBusinessCategories();
  const options = useQuery({ queryKey: ['business-requests', 'options'], queryFn: businessRequestsApi.options });
  const [form, setForm] = useState<NewBusinessForm>(EMPTY_NEW_BUSINESS);
  const [administers, setAdministers] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const mine = useQuery({ queryKey: ['business-requests', 'mine'], queryFn: () => businessRequestsApi.list({ mine: true }) });
  const [sendError, setSendError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: businessRequestsApi.create,
    onSuccess: () => navigate('/register/sent', { replace: true }),
    onError: (e) => setSendError(sendErrorText(e)),
  });

  const estate = isEstateCategory(categories, form.business_category_id);
  const issues = registerIssues(form, estate, administers);
  const latest = latestBusinessRequest(mine.data ?? []);
  // Denied: the form comes back with what was sent (the list has no payload; the detail does), to fix and resend.
  const denied = latest?.status === 'rejected' ? latest : null;
  const deniedDetail = useQuery({
    queryKey: ['business-requests', denied?.id],
    queryFn: () => businessRequestsApi.get(denied!.id),
    enabled: !!denied,
  });
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const d = deniedDetail.data;
    if (prefilled || !d) return;
    setPrefilled(true);
    setForm(formFromPayload(d.payload));
    if (d.is_estate && d.estate_group_id != null) setAdministers(d.estate_group_id);
  }, [deniedDetail.data, prefilled]);
  if (mine.data && ownerStep(latest) === 'sent' && latest?.status !== 'rejected') return <Navigate to="/register/sent" replace />;

  const onSend = () => {
    setTouched(true);
    if (issues.length) return;
    setSendError(null);
    send.mutate(businessRequestBody(form, { estate, administersGroupId: administers, ownerName }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--surface-2,#f5f5f5)]">
      <header className="shrink-0 bg-white border-b border-solid border-x-0 border-t-0 border-[var(--line)]">
        <div className="flex items-center gap-3 px-4 md:px-6 py-3 md:py-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="m-0 text-[18px] md:text-[20px] font-bold text-[var(--ink-900)]">Register your business</h1>
            <p className="m-0 text-[13px] text-[var(--ink-500)]">{tenantName || 'The community'} reviews it before it goes live. Hours, team and services come after.</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={logOut}
              disabled={send.isPending}
              className="flex-1 sm:flex-none h-11 px-4 rounded-[12px] border border-solid border-[var(--line)] bg-white text-[15px] font-semibold font-[inherit] text-[var(--ink-700)] cursor-pointer"
            >
              Log out
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={send.isPending}
              className="flex-1 sm:flex-none h-11 px-[18px] rounded-[12px] border-0 bg-[var(--tenant-primary,#005488)] text-white text-[15px] font-semibold font-[inherit] cursor-pointer inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {send.isPending && <Loader2 size={16} className="animate-spin" aria-hidden />}
              Send request
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-5">
          {latest?.status === 'rejected' && latest.note && (
            <section className={newBusinessCard}>
              <h2 className="m-0 mb-1 text-[16px] font-bold text-[var(--ink-900)]">Your last request was denied</h2>
              <p className="m-0 text-[14px] text-[var(--ink-700)]">{latest.note}</p>
            </section>
          )}
          {sendError && <div role="alert" className="text-[14px] text-[var(--danger,#b42318)]">{sendError}</div>}
          <NewBusinessFields
            form={form}
            setForm={setForm}
            touched={touched}
            categoryExtra={estate ? (
              <div className={`fc ${touched && issues.includes('estate_group') ? 'err' : ''}`} style={{ marginBottom: 0 }}>
                <label htmlFor="rb-eg">Which estate group?<span className="req"> *</span></label>
                <select id="rb-eg" value={administers ?? ''} onChange={(e) => setAdministers(e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">Select a group…</option>
                  {(options.data?.estate_groups ?? []).map((g) => (
                    <option key={g.id} value={g.id} disabled={g.has_admin_business}>
                      {g.has_admin_business ? `${g.name} · has an administrator` : g.name}
                    </option>
                  ))}
                </select>
                {touched && issues.includes('estate_group') && <div className="msg" style={{ display: 'block' }}>Pick the estate group the business administers</div>}
              </div>
            ) : null}
          />
        </div>
      </main>
    </div>
  );
};

/** W4 — Request sent. Once approved the owner's token gets its groups: refresh it and enter. */
const RequestSent: React.FC = () => {
  const navigate = useNavigate();
  const { tenantName, tenantPrimaryColor } = useTenantBranding();
  const community = tenantName || 'The community';
  const mine = useQuery({ queryKey: ['business-requests', 'mine'], queryFn: () => businessRequestsApi.list({ mine: true }) });
  const latest = latestBusinessRequest(mine.data ?? []);
  const approved = latest?.status === 'approved';

  useEffect(() => {
    if (!approved) return;
    authService.refreshToken().then(() => window.location.assign('/')).catch(() => logOut());
  }, [approved]);

  if (mine.data && !latest) return <Navigate to="/register/business" replace />;
  const name = latest?.business_name || latest?.title || 'your business';
  const view = latest ? statusView(latest.ui_status) : null;
  return (
    <AuthShell>
      <h1 className="m-0 text-[24px] font-bold text-[var(--ink-900)]">
        {latest?.status === 'rejected' ? 'Request denied' : 'Request sent'}
      </h1>
      {mine.isLoading && <p className="m-0 text-[14px] text-[#6B7280]">Loading…</p>}
      {latest && (
        <>
          <p className="m-0 text-[14px] text-[#6B7280]">
            {latest.status === 'rejected'
              ? latest.note || `${community} did not approve it.`
              : `${community} is reviewing ${name}. We will email you when it is approved.`}
          </p>
          {view && <span><StatusChip tone={view.tone} label={view.label} /></span>}
        </>
      )}
      {mine.isError && <ErrorText>Your request could not be loaded. Try again in a moment.</ErrorText>}
      {latest?.status === 'rejected' && (
        <button type="button" className={primaryBtn} style={{ backgroundColor: tenantPrimaryColor || '#005488' }} onClick={() => navigate('/register/business')}>
          Edit and send again
        </button>
      )}
      <button type="button" className={tertiaryBtn} onClick={logOut}>Log out</button>
    </AuthShell>
  );
};

const OwnerOnly: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  (useOwnerSession() ? <>{children}</> : <Navigate to="/register" replace />);

export const RegisterRoutes: React.FC = () => (
  <Routes>
    <Route path="/register" element={<CreateAccount />} />
    <Route path="/register/business" element={<OwnerOnly><RegisterForm /></OwnerOnly>} />
    <Route path="/register/sent" element={<OwnerOnly><RequestSent /></OwnerOnly>} />
    <Route path="*" element={<Navigate to="/register" replace />} />
  </Routes>
);

export default RegisterRoutes;
